import React, { useState, useMemo } from 'react';
import { useMasterData } from '../../hooks/useMasterData';
import {
  PresentationChartLineIcon,
  DocumentChartBarIcon,
  UsersIcon,
  TruckIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { PageTitle } from '../../components/ui/PageTitle';
import {
  calculateMonthlySales,
  calculateSalesByClient,
  calculateDriverPerformance,
  filterCasesByMonth,
  filterCompletedCases,
  // generateBillingData, // 未使用のため一時的にコメントアウト
  getMonthName,
  // getStatusText // 未使用のため一時的にコメントアウト
} from '../../utils/reportUtils';
import { exportToCSV } from '../../utils/csvExport';
import { Button } from '../../components/ui';

const StatCard: React.FC<{
  title: string;
  value: string;
  icon: React.ReactNode;
  color?: string;
}> = ({ title, value, icon, color = 'primary' }) => (
  <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md'>
    <div className='flex items-center'>
      <div className={`p-3 rounded-full bg-${color}-100 dark:bg-${color}-900/50 mr-4`}>{icon}</div>
      <div>
        <p className='text-sm text-gray-500 dark:text-gray-400'>{title}</p>
        <p className='text-2xl font-bold text-gray-800 dark:text-white'>{value}</p>
      </div>
    </div>
  </div>
);

const ReportPage: React.FC = () => {
  const { cases, clients, landTransporters } = useMasterData();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // 完了案件のみをフィルタリング
  const completedCases = useMemo(() => filterCompletedCases(cases), [cases]);

  // 月間データの計算
  const monthlyData = useMemo(() => {
    const monthlyCases = filterCasesByMonth(completedCases, selectedYear, selectedMonth);
    const monthlySales = calculateMonthlySales(monthlyCases);
    const salesByClient = calculateSalesByClient(monthlyCases, (id: string) => clients.find(c => c.id === id));

    return {
      cases: monthlyCases,
      sales: monthlySales,
      clientRanking: salesByClient.slice(0, 5),
    };
  }, [completedCases, clients, selectedYear, selectedMonth]);

  // 陸送員パフォーマンスの計算
  const driverPerformance = useMemo(() => {
    const monthlyCases = filterCasesByMonth(cases, selectedYear, selectedMonth);
    return calculateDriverPerformance(monthlyCases, landTransporters);
  }, [cases, landTransporters, selectedYear, selectedMonth]);

  // 年月の選択肢を生成
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, []);

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  }, []);

  // CSV出力関数
  const handleExportCasesCSV = () => {
    const data = {
      headers: ['顧客名', '案件数'],
      rows: monthlyData.clientRanking.map(client => [client.clientName, client.caseCount]),
      filename: `案件レポート_${selectedYear}年${selectedMonth}月`,
    };
    exportToCSV(data);
  };

  const handleExportDriverPerformanceCSV = () => {
    const data = {
      headers: ['陸送員名', '総案件数', '完了案件数', '完了率(%)'],
      rows: driverPerformance.map(driver => [
        driver.driverName,
        driver.totalCases,
        driver.completedCases,
        Math.round(driver.completionRate),
      ]),
      filename: `陸送員実績_${selectedYear}年${selectedMonth}月`,
    };
    exportToCSV(data);
  };

  return (
    <div className='max-w-7xl mx-auto'>
      <PageTitle icon={PresentationChartLineIcon} title='レポート' />

      {/* フィルタリングコントロール */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <div>
            <label
              htmlFor='year'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              年
            </label>
            <select
              id='year'
              value={selectedYear}
              onChange={e => setSelectedYear(parseInt(e.target.value))}
              className='w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500'
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}年
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor='month'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              月
            </label>
            <select
              id='month'
              value={selectedMonth}
              onChange={e => setSelectedMonth(parseInt(e.target.value))}
              className='w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500'
            >
              {months.map(month => (
                <option key={month} value={month}>
                  {getMonthName(month)}
                </option>
              ))}
            </select>
          </div>
          <div className='flex items-end'>
            <Button
              onClick={handleExportCasesCSV}
              variant='primary'
              size='md'
              className='inline-flex items-center !bg-green-600 !border-green-600 dark:!bg-green-600 dark:!border-green-600 hover:!bg-green-700 dark:hover:!bg-green-700'
            >
              <ArrowDownTrayIcon className='h-5 w-5 mr-2' />
              案件データCSV出力
            </Button>
          </div>
        </div>
      </div>

      {/* 統計カード */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
        <StatCard
          title='月間完了案件'
          value={`${monthlyData.cases.length}件`}
          icon={<DocumentChartBarIcon className='h-6 w-6 text-blue-600' />}
          color='blue'
        />
        <StatCard
          title='アクティブ顧客'
          value={`${clients.filter(c => c.status === 'Active').length}社`}
          icon={<UsersIcon className='h-6 w-6 text-purple-600' />}
          color='purple'
        />
        <StatCard
          title='対応可能陸送員'
          value={`${landTransporters.filter(d => d.status === 'Available').length}名`}
          icon={<TruckIcon className='h-6 w-6 text-orange-600' />}
          color='orange'
        />
      </div>

      {/* 顧客別案件数ランキング */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8'>
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
          <h2 className='text-lg font-semibold text-gray-800 dark:text-white mb-4'>
            顧客別案件数ランキング (TOP5)
          </h2>
          <div className='space-y-4'>
            {monthlyData.clientRanking.map((client, index) => (
              <div
                key={client.clientId}
                className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded'
              >
                <div className='flex items-center'>
                  <span className='text-lg font-bold text-gray-600 dark:text-gray-300 mr-3'>
                    {index + 1}
                  </span>
                  <div>
                    <p className='font-medium text-gray-800 dark:text-white'>{client.clientName}</p>
                  </div>
                </div>
                <div className='text-right'>
                  <p className='font-bold text-gray-800 dark:text-white'>{client.caseCount}件</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 陸送員パフォーマンス */}
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
          <div className='flex justify-between items-center mb-4'>
            <h2 className='text-lg font-semibold text-gray-800 dark:text-white'>
              陸送員パフォーマンス
            </h2>
            <Button
              onClick={handleExportDriverPerformanceCSV}
              variant='primary'
              size='sm'
              className='inline-flex items-center px-3 py-1 !bg-green-600 !border-green-600 dark:!bg-green-600 dark:!border-green-600 hover:!bg-green-700 dark:hover:!bg-green-700 text-sm'
            >
              <ArrowDownTrayIcon className='h-4 w-4 mr-1' />
              CSV
            </Button>
          </div>
          <div className='space-y-3'>
            {driverPerformance.slice(0, 5).map(driver => (
              <div
                key={driver.driverId}
                className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded'
              >
                <div>
                  <p className='font-medium text-gray-800 dark:text-white'>{driver.driverName}</p>
                </div>
                <div className='text-right'>
                  <p className='font-bold text-gray-800 dark:text-white'>
                    {driver.completedCases}/{driver.totalCases}件 (
                    {Math.round(driver.completionRate)}%)
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
